import React from 'react';
import { Box, TextField, MenuItem, Typography, Button, CircularProgress } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import { usePhysicalCountPanel } from './PhysicalCountPanel.hooks';
import {
  StepFormRow,
  ComparisonSectionTitle,
  ComparisonStatsGrid,
  ComparisonStatCard,
  ComparisonStatLabel,
  ComparisonStatValue,
} from './PhysicalCountPanel.styles';
import {
  PHYSICAL_COUNT_METRICS,
  type PhysicalCountMetricKey,
} from '../physicalCount/physicalCountMetrics.config';
import PhysicalCountMetricInfoButton from '../physicalCount/PhysicalCountMetricInfoButton';
import { CloseConfirmationModal } from '../../modals/confirmation';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import * as S from '../../sales/orders/ExchangeModal/ExchangeDrawer.styles';

const PANEL_METRIC_ACCENTS: Record<PhysicalCountMetricKey, (theme: Theme) => string> = {
  expected: (theme) => theme.palette.text.secondary,
  scanned: (theme) => theme.palette.info.main,
  ok: (theme) => theme.palette.success.main,
  missing: (theme) => theme.palette.error.main,
  excess: (theme) => theme.palette.warning.main,
  duplicate: (theme) => theme.palette.warning.dark,
  notAvailable: (theme) => theme.palette.secondary.main,
};

const DRAWER_PAPER_SX = {
  width: { xs: '100%', sm: '800px', md: '960px' },
  maxWidth: '960px',
} as const;

function InlineNotice({
  severity,
  children,
  sx,
}: {
  severity: 'info' | 'warning' | 'error';
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const color =
    severity === 'info'
      ? theme.palette.info.main
      : severity === 'warning'
        ? theme.palette.warning.main
        : theme.palette.error.main;
  return (
    <Typography
      component="div"
      variant="body2"
      sx={{
        color,
        bgcolor: 'transparent',
        py: 0.5,
        '& strong': { color: 'inherit', fontWeight: 600 },
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

export interface PhysicalCountPanelProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  locations: Array<{ id: number; name: string }>;
}

const PhysicalCountPanel: React.FC<PhysicalCountPanelProps> = ({
  open,
  onClose,
  onSuccess,
  locations,
}) => {
  const theme = useTheme();
  const {
    currentStep,
    totalSteps,
    state,
    updateState,
    handleCloseClick,
    handleConfirmClose,
    handleCancelClose,
    showCloseConfirmation,
    handleNextStep,
    handlePreviousStep,
    handleSave,
    isNextDisabled,
    isSaveDisabled,
    saving,
    previewLoading,
    saveError,
    uploadFile,
    setUploadFile,
    preview,
    fileInputKey,
  } = usePhysicalCountPanel({ open, onClose, onSuccess });

  useEscapeKey(
    () => {
      if (open && !saving && !previewLoading && !showCloseConfirmation) {
        handleCloseClick();
      }
    },
    open && !saving && !previewLoading && !showCloseConfirmation,
  );

  const progressPct = Math.round((currentStep / totalSteps) * 100);
  const locationName = locations.find((l) => String(l.id) === state.locationId)?.name ?? '—';

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepFormRow>
            <TextField
              select
              fullWidth
              label="Sucursal"
              value={state.locationId}
              onChange={(e) => updateState({ locationId: e.target.value })}
              required
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Seleccionar sucursal</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={String(loc.id)}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              type="date"
              label="Fecha del conteo"
              value={state.sessionDate}
              disabled
              InputLabelProps={{ shrink: true }}
              helperText="Se asigna automáticamente al día de hoy"
            />
          </StepFormRow>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.success.main, 0.14)
                    : '#f0fdf4',
                border: '1px solid',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.success.main, 0.4)
                    : '#bbf7d0',
                borderRadius: '12px',
                p: 1.5,
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Sucursal: <strong>{locationName}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Fecha: <strong>{state.sessionDate}</strong>
              </Typography>
              {preview != null && (
                <Typography variant="caption" color="text.secondary">
                  Ítems esperados: <strong>{preview.summary.expectedCount}</strong>
                </Typography>
              )}
            </Box>

            <InlineNotice severity="info">
              Suba un archivo <strong>CSV</strong> o <strong>XLSX</strong> con los códigos
              escaneados en <strong>formato vertical</strong>: un código por fila, en una sola
              columna.
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  mb: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  whiteSpace: 'pre',
                }}
              >
                {`ABC001\nABC002\nABC003`}
              </Box>
              No incluya encabezados ni columnas adicionales.
            </InlineNotice>

            <Button
              variant="outlined"
              component="label"
              sx={{ borderRadius: '10px', textTransform: 'none', py: 1.25 }}
              disabled={previewLoading || saving}
            >
              {uploadFile != null ? 'Reemplazar archivo' : 'Seleccionar archivo'}
              <input
                key={fileInputKey}
                hidden
                type="file"
                accept=".csv,.xlsx"
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  setUploadFile(file);
                  event.target.value = '';
                }}
              />
            </Button>

            {uploadFile != null && (
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '12px',
                }}
              >
                <Typography variant="body2">
                  Archivo: <strong>{uploadFile.name}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(uploadFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}

            {previewLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Validando archivo…
                </Typography>
              </Box>
            )}

            {preview != null && !previewLoading && (
              <Box component="section">
                <ComparisonSectionTitle component="h3">
                  Vista previa de la comparación
                </ComparisonSectionTitle>
                <ComparisonStatsGrid>
                  {PHYSICAL_COUNT_METRICS.map((metric) => {
                    const accent = PANEL_METRIC_ACCENTS[metric.key](theme);
                    const label = metric.panelLabel;
                    const value = metric.getPreviewValue(preview.summary);

                    return (
                      <ComparisonStatCard key={metric.key} $accent={accent}>
                        <ComparisonStatLabel>{label}</ComparisonStatLabel>
                        <ComparisonStatValue $accent={accent}>{value}</ComparisonStatValue>
                        <PhysicalCountMetricInfoButton tooltip={metric.tooltip} label={label} />
                      </ComparisonStatCard>
                    );
                  })}
                </ComparisonStatsGrid>
              </Box>
            )}

            {preview != null && !previewLoading && (
              <InlineNotice severity="warning">
                Al pulsar <strong>Guardar sesión</strong> se registrará y cerrará la sesión de
                conteo. Podrá ver el detalle completo en el listado.
              </InlineNotice>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  const isLastStep = currentStep === totalSteps;
  const isFirstStep = currentStep === 1;
  const canGoBack = currentStep > 1;
  const footerBusy = saving || previewLoading;

  return (
    <>
      <S.StyledDrawer
        anchor="right"
        open={open}
        onClose={handleCloseClick}
        sx={{ '& .MuiDrawer-paper': DRAWER_PAPER_SX }}
        PaperProps={{ sx: DRAWER_PAPER_SX }}
      >
        <S.DrawerContainer>
          <S.DrawerHeader>
            <S.DrawerTitle component="h2">Nueva sesión de conteo físico</S.DrawerTitle>
            <S.CloseButton onClick={handleCloseClick} aria-label="Cerrar">
              <CloseIcon />
            </S.CloseButton>
          </S.DrawerHeader>

          <S.ProgressContainer>
            <S.ProgressBar>
              <S.ProgressBarFill style={{ width: `${progressPct}%` }} />
            </S.ProgressBar>
            <S.ProgressInfo>
              <span>
                Paso {currentStep} de {totalSteps}
              </span>
              <span>{progressPct}% completado</span>
            </S.ProgressInfo>
          </S.ProgressContainer>

          <S.ContentContainer>
            {saveError != null && (
              <InlineNotice severity="error" sx={{ mb: 2 }}>
                {saveError}
              </InlineNotice>
            )}
            {renderStepContent()}
          </S.ContentContainer>

          <S.DrawerFooter>
            {isFirstStep ? (
              <S.PreviousButton variant="tonal" onClick={handleCloseClick} disabled={saving}>
                Cancelar
              </S.PreviousButton>
            ) : canGoBack ? (
              <S.PreviousButton variant="tonal" onClick={handlePreviousStep} disabled={footerBusy}>
                Anterior
              </S.PreviousButton>
            ) : (
              <Box sx={{ minWidth: 120 }} />
            )}

            <S.FooterButtonsContainer>
              {!isLastStep ? (
                <S.NextButton
                  variant="contained"
                  onClick={handleNextStep}
                  disabled={isNextDisabled}
                >
                  Siguiente
                </S.NextButton>
              ) : (
                <S.ConfirmButton
                  variant="contained"
                  onClick={() => void handleSave()}
                  disabled={isSaveDisabled}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {saving ? 'Procesando...' : 'Guardar sesión'}
                </S.ConfirmButton>
              )}
            </S.FooterButtonsContainer>
          </S.DrawerFooter>
        </S.DrawerContainer>
      </S.StyledDrawer>

      <CloseConfirmationModal
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </>
  );
};

export default PhysicalCountPanel;
